import type { TemplateBlock, TemplatePage } from '@solar/api-client';

import { evaluateCondition, resolvePath } from './condition';

/** 조건(showIf)을 평가해 실제로 표시할 페이지만 남긴다. */
export function visiblePages(pages: TemplatePage[], data: unknown): TemplatePage[] {
  return pages.filter((page) => evaluateCondition(page.showIf, data));
}

/**
 * 표시되는 페이지에서 요구되는 서명 슬롯.
 * 서버의 `required_signature_slots` 와 결과가 같아야 한다.
 */
export function requiredSlots(pages: TemplatePage[], data: unknown): string[] {
  const slots: string[] = [];
  for (const page of visiblePages(pages, data)) {
    for (const block of page.blocks) {
      // required:false 슬롯(2026 서식의 시공업체 직인)은 시민이 혼자 서명해도 제출되게 뺀다.
      if (block.type === 'signature' && block.required !== false && !slots.includes(block.slot)) {
        slots.push(block.slot);
      }
    }
  }
  return slots;
}

/**
 * 하나의 서명을 여러 슬롯에 복사한다.
 *
 * 레거시 OZ 서식은 SignPad0 에 서명하면 스크립트가 SignPad1~9 에 이미지를
 * 그대로 복사했다. 페이지마다 다시 서명하게 하면 실사용에서 이탈률이 급증하므로
 * 동작 자체는 유지하되, **어떤 슬롯으로 복사되는지 서식 정의에 명시**한다.
 */
export function propagationTargets(pages: TemplatePage[], slot: string): string[] {
  for (const page of pages) {
    for (const block of page.blocks) {
      if (block.type === 'signature' && block.slot === slot) {
        return block.propagate ?? [];
      }
    }
  }
  return [];
}

/**
 * 자치구 동적 라벨.
 *
 * 레거시 OZ 서식(SolarMini_1.ozr)은 OnBind 에서 SQL_ORDER.addr2 가 아래 6개 구
 * 중 하나면 "자치구보조" 라벨을 "성동구보조" 등으로 치환했다 (docs/04-eform.md §5
 * — 시 단독지원 자치구와 시+구 매칭지원 자치구를 서식 한 벌로 처리).
 *
 * 서버(app/services/template_service.py — GU_LABEL_TARGETS / resolve_addr_gu /
 * localize_definition)와 **동일한 규칙**이어야 한다. 서버는 뷰 응답과 PDF 를
 * 같은 함수로 치환해 내려주고, 여기의 치환은 이미 치환된 문안에는 아무 일도
 * 하지 않는(멱등) 안전망이다 — 셋 중 하나만 어긋나면 화면과 PDF 가 달라진다.
 */
export const GU_LABEL_TARGETS: readonly string[] = [
  '성동구',
  '은평구',
  '금천구',
  '동작구',
  '송파구',
  '구로구',
];

/** 목록 밖 자치구(시 단독지원)의 일반 문구 — 레거시는 라벨을 바꾸지 않았다. */
export const DEFAULT_GU_LABEL = '자치구';

const ADDR_GU_PLACEHOLDER = /\{\{\s*addr_gu\s*\}\}/g;

export function resolveAddrGu(data: unknown): string {
  const addr2 = resolvePath(data, 'order.addr2');
  if (typeof addr2 === 'string' && GU_LABEL_TARGETS.includes(addr2.trim())) {
    return addr2.trim();
  }
  return DEFAULT_GU_LABEL;
}

/** 서식 문안({{addr_gu}} 플레이스홀더)을 주문 자치구로 치환한다. */
export function interpolateAddrGu(text: string, data: unknown): string;
export function interpolateAddrGu(text: string | undefined, data: unknown): string | undefined;
export function interpolateAddrGu(text: string | undefined, data: unknown): string | undefined {
  if (text === undefined) return undefined;
  return text.replace(ADDR_GU_PLACEHOLDER, resolveAddrGu(data));
}

export function formatValue(value: unknown, format?: 'won'): string {
  if (value === null || value === undefined || value === '') return '-';
  if (format === 'won') {
    const num = Number(value);
    return Number.isFinite(num) ? `${num.toLocaleString('ko-KR')}원` : '-';
  }
  return String(value);
}

export function blockValue(block: Extract<TemplateBlock, { type: 'fields' }>, data: unknown) {
  return block.fields.map((field) => ({
    // 자치구 라벨 치환 — 값(text)이 아니라 라벨만 대상이다.
    label: interpolateAddrGu(field.label, data),
    text: formatValue(resolvePath(data, field.path), field.format),
  }));
}

export function tableRows(
  block: Extract<TemplateBlock, { type: 'table' }>,
  data: unknown,
): Array<Record<string, string>> {
  const rows = resolvePath(data, block.path);
  if (!Array.isArray(rows)) return [];
  return rows.map((row: Record<string, unknown>) =>
    Object.fromEntries(block.columns.map((c) => [c.key, formatValue(row[c.key])])),
  );
}
